interface ShareService {
  name: string;
  share: (articleId: string) => void;
}

interface ShareContext {
  services: ShareService[];
}

interface Config {
  facebookClientId: string;
}

interface ArticleSummary {
  title: string;
  shareUrl: string;
  excerpt: string;
  leadImage: {
    url: string;
  };
  shareMessages: {
    facebook?: string;
    twitter?: string;
    linkedIn?: string;
    default?: string;
    email?: string;
  };
}

const fetchConfig = async (): Promise<Config> => {
  const response = await fetch("/api/config");
  return (await response.json()) as Config;
};

const fetchArticleSummary = async (articleId: string): Promise<ArticleSummary> => {
  const response = await fetch(`/api/articles/${articleId}`);
  return (await response.json()) as ArticleSummary;
};

const articleShared = (articleId: string, source: string) => {
  fetch(`/api/articles/${articleId}/shared`, {
    body: JSON.stringify({ source }),
    method: "PUT",
    headers: {
      "content-type": "application/json",
    },
  });
};

const shareContext: ShareContext = {
  services: [
    {
      name: "facebook",
      share: async (articleId) => {
        const config = await fetchConfig();
        const articleSummary = await fetchArticleSummary(articleId);
        const shareOptions = {
          method: "feed",
          app_id: config.facebookClientId,
          link: articleSummary.shareUrl,
          name:
            articleSummary.shareMessages.facebook ||
            articleSummary.shareMessages.default ||
            articleSummary.title,
          description: articleSummary.excerpt,
          caption: "inkl.com",
          picture: articleSummary.leadImage && articleSummary.leadImage.url,
        };
        window.FB.ui(shareOptions);
        articleShared(articleId, "facebook");
      },
    },
    {
      name: "twitter",
      share: async (articleId) => {
        const articleSummary = await fetchArticleSummary(articleId);
        const text =
          articleSummary.shareMessages.twitter ||
          articleSummary.shareMessages.default ||
          articleSummary.title;

        const urlParams: URLSearchParams = new URLSearchParams({
          url: articleSummary.shareUrl,
          text,
        });

        const openUrl = `https://twitter.com/intent/tweet?${urlParams}`;
        const height = 253;
        const width = 570;
        const left = screen.width / 2 - width / 2;
        const top = screen.height / 2 - height / 2;
        const features = `menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=${height},width=${width},top=${top},left=${left}`;
        window.open(openUrl, "", features);
        articleShared(articleId, "twitter");
      },
    },
    {
      name: "linkedin",
      share: async (articleId) => {
        const articleSummary = await fetchArticleSummary(articleId);
        const title =
          articleSummary.shareMessages.linkedIn ||
          articleSummary.shareMessages.default ||
          articleSummary.title;

        const urlParams: URLSearchParams = new URLSearchParams({
          url: articleSummary.shareUrl,
          title,
          summary: articleSummary.excerpt,
          mini: "true",
          source: "inkl.com",
        });

        const openUrl = `https://www.linkedin.com/shareArticle?${urlParams}`;
        const height = 520;
        const width = 570;
        const left = screen.width / 2 - width / 2;
        const top = screen.height / 2 - height / 2;
        const features = `menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=${height},width=${width},top=${top},left=${left}`;
        window.open(openUrl, "", features);
        articleShared(articleId, "linked-in");
      },
    },
    {
      name: "email",
      share: async (articleId) => {
        const articleSummary = await fetchArticleSummary(articleId);
        const title =
          articleSummary.shareMessages.email ||
          articleSummary.shareMessages.default ||
          articleSummary.title;

        window.location.href = `mailto:?Subject=${title}&Body=${articleSummary.excerpt}%0A%0A${articleSummary.shareUrl}`;
        articleShared(articleId, "email");
      },
    },
  ],
};

const createEventListenerForShareButton = (element: Element) => {
  const service = shareContext.services.find(
    (service) => element.getAttribute(`share-${service.name}`) != null
  );

  if (!service) {
    throw new Error("Can not find service for element. This is a bug.");
  }

  const articleId = element.getAttribute("data-article-id");

  if (!articleId) {
    throw new Error("Can not resolve article id for share. This is a bug.");
  }

  if (!element.parentElement) {
    throw new Error("Can not resolve parent element. This is a bug.");
  }

  element.parentElement.addEventListener("click", () => service.share(articleId));
};

document.addEventListener("DOMContentLoaded", () => {
  const serviceSelector = shareContext.services
    .map((service) => `[share-${service.name}]`)
    .join(", ");

  document.querySelectorAll(serviceSelector).forEach(createEventListenerForShareButton);
});
